// MaterialRegions — Set12 §3.3: the `MapObject → MaterialRegion` bridge (mirrors the shape of
// `collectRasterFeatures` in ConstraintRasterizer.ts). Produces the region list the splat
// rasterizer consumes: per-path `materialFeature` + its world-space polylines + the AUTHORITATIVE
// closed flag + RESOLVED dominance + a broad-phase bbox. Also home of the per-region
// SIGNED-DISTANCE helpers (§3.3) — the rasterizer emits signed distances in metres (negative =
// inside) rather than hard weights, and the fragment shader feathers them (P3).
//
// Anti-drift rule (the Set1 B3/B4 bug class — plan §3.3/§8): `MaterialRegion` WRAPS the source
// feature (`pathFeature: PathMaterialFeature`) instead of copying its fields — a new field on the
// feature needs NO bridge edit (read it from `region.pathFeature`). The ONLY fields the bridge
// resolves (and therefore owns) are: id, polylines, closed, dominance, bbox.
import {pointInPolygon} from "../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer.js";
import type {MapObject_ForTerrain} from "../../Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain.js";
import type {Pt} from "../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/RasterFeatures_Pure.js";
import {PathMaterialFeature} from "../@MaterialFeature.js";
import type {MaterialMask} from "../@MaterialFeature.js";
import {Rect} from "../../../Utils/ThreeJS/VectorStructs.js";
import {regionContainmentDepth_Gen, resolveRegionDominances} from "./RegionDominance.js";
import {timeIt} from "../../Scenery/Render/SceneryTiming.js";

/** A material region: one active `PathMaterialFeature` + the bridge-resolved geometry. All
 *  authoring fields (recipeID, mode, strokeWidth, feather, featherCurve, opaque, edgeNoise,
 *  paramModulations, masks) are read from `pathFeature` — NEVER copied up (see file docblock). */
export interface MaterialRegion {
	/** path_id (stable; drives RNG seeding + region rows). Falls back to `${objID}_path${index}`
	 *  for legacy paths with no path_id (deterministic — obj ids are stable). */
	id: string;
	/** The authored source, verbatim. */
	pathFeature: PathMaterialFeature;
	/** World-space polylines from `_meshSet_world` (a path can produce several meshes). */
	polylines: Pt[][];
	/** `_curve_closed_resolved` (AUTHORITATIVE — NOT `path.curve_closed`; inheritance can flip it). */
	closed: boolean;
	/** RESOLVED dominance: `pathFeature.dominance ?? derived containment depth` (§3.4). The one
	 *  field that legitimately shadows the feature, because deriving it needs the whole region set. */
	dominance: number;
	/** Broad-phase bbox over all polylines, inflated by `feather + edgeNoise.amplitude` (§3.3).
	 *  The rasterizer filters regions per chunk by this before evaluating per-sample distances. */
	bbox: Rect;
}

// ============================================================
// Bridge
// ============================================================

/** Collect the material regions from packaged terrain objects: every path with an ACTIVE
 *  `materialFeature` (`!disabled && recipeID != ""`). Drops:
 *   - paths with no usable world geometry (`_meshSet_world` absent or all polylines < 2 points);
 *   - `mode: "fill"` / `"fillAndStroke"` on an OPEN path — no interior exists (a data error —
 *     warned, dropped rather than inventing a stroke width, §3.2 mode doc).
 *  Output is sorted deterministically: dominance DESC (higher wins — paint-over iterates
 *  ascending at rasterise time), ties by `id` ASC (plan §3.4 "ties break by path_id"). */
export function collectMaterialRegions(objects: ReadonlyArray<MapObject_ForTerrain>): MaterialRegion[] {
	const regions: MaterialRegion[] = [];
	for (const obj of objects) {
		for (const [index, path] of (obj.paths ?? []).entries()) {
			const mf = path.materialFeature;
			if (!PathMaterialFeature.IsActive(mf)) continue;
			const polylines = (path._meshSet_world ?? []).filter(poly=>poly.length >= 2);
			if (polylines.length == 0) continue;
			const closed = !!path._curve_closed_resolved;
			const mode = mf.mode;
			if ((mode == "fill" || mode == "fillAndStroke") && !closed) {
				console.warn(`[collectMaterialRegions] path "${path.path_id ?? `${obj.id}/path${index}`}" has materialFeature mode "${mode}" but is not closed — no interior exists; region dropped (Set12 §3.3).`);
				continue;
			}
			// Broad-phase bbox: over all polylines, inflated by feather + edgeNoise amplitude.
			let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
			for (const poly of polylines) {
				for (const p of poly) {
					if (p.x < minX) minX = p.x;
					if (p.y < minY) minY = p.y;
					if (p.x > maxX) maxX = p.x;
					if (p.y > maxY) maxY = p.y;
				}
			}
			// NOTE: `mf.feather` may be absent on persisted data (the whitelist forwards the
			// feature as a spread, so class defaults don't apply) — fall back to the class
			// default here for the bbox inflation. The rasterizer's broad-phase re-inflates the
			// CHUNK bounds by the resolved feather (per-map `blendDefaults` layered there), so
			// an under-inflated bbox can never drop an influencing region (§3.3 step 1).
			const inflate = (mf.feather ?? 2) + (mf.edgeNoise?.amplitude ?? 0);
			const id = path.path_id || `${obj.id}_path${index}`;
			// 2026-08-26 — MERGE same-id regions: a duplicated map-object (same `path_id` twice
			// in the DB) emitted two regions under one id, and the rasterizer's per-id slot
			// bookkeeping (assignRegionSlots's dedupe/collapse keyed by region id) corrupted the
			// slot/distance fields for chunks straddling the duplicate → black chunks. The
			// collector's region semantics are already "inside ANY polygon = inside the region",
			// so a same-id re-emission unions its polylines into the EXISTING region instead of
			// creating a second entry. (Deep zoom exposed it; the material-edit CHURN the merge
			// also fixes was the same collision re-rasterised on every edit.)
			const existing = regions.find(r=>r.id == id);
			if (existing) {
				existing.polylines.push(...polylines);
				// Capture the existing bbox's ABSOLUTE max BEFORE any min-side lowering below
				// (the "keep the max side fixed" invariant).  If we read `existing.bbox.x +
				// existing.bbox.width` AFTER lowering `bbox.x`, the old width applied to the NEW
				// lower x shifts the max LEFT by the delta — truncating the bbox so a merged
				// region's far side under-covers its own polylines (a grass fill whose polygon
				// extends past the bbox max → `filterRegionsForChunk`'s bbox.Intersects drops it
				// → the chunk near that edge renders the base material instead of grass; deep
				// zoom exposed it 2026-08-26).
				const oldMaxX = existing.bbox.x + existing.bbox.width;
				const oldMaxY = existing.bbox.y + existing.bbox.height;
				if (minX - inflate < existing.bbox.x) existing.bbox.x = minX - inflate;
				if (minY - inflate < existing.bbox.y) existing.bbox.y = minY - inflate;
				const right = Math.max(oldMaxX, maxX + inflate);
				const top = Math.max(oldMaxY, maxY + inflate);
				existing.bbox.width = right - existing.bbox.x;
				existing.bbox.height = top - existing.bbox.y;
				continue;
			}
			regions.push({
				id,
				pathFeature: mf,
				polylines,
				closed,
				dominance: 0, // resolved below (needs the whole set)
				bbox: new Rect(minX - inflate, minY - inflate, (maxX - minX) + inflate * 2, (maxY - minY) + inflate * 2, false),
			});
		}
	}
	resolveDominances(regions);
	return regions;
}

/** Resolve each region's `dominance` (§3.4): explicit `pathFeature.dominance` wins; else derived
 *  = containment depth for interior-bearing regions (fill / fillAndStroke — count how many OTHER
 *  interior-bearing regions strictly contain this region), and "above all fills" for strokes (a
 *  road crossing a field sits on top; overridable). Cycles are impossible (containment is a
 *  partial order). Deterministic sort afterwards: dominance DESC, id ASC.
 *
 *  The dominance/containment machinery is GENERIC (see `RegionDominance.ts`) — also used by the
 *  scenery bridge for `PathSceneryFeature.dominance`/`suppressPriorScenery` (scenery-only nesting).
 *
 *  NOTE — containment is tested via the region's BOUNDARY VERTICES (all of a region's polylines'
 *  vertices strictly inside the candidate container), NOT via its centroid as the plan's §3.4
 *  parenthetical literally suggests.  The centroid test is broken for the PRIMARY use case
 *  (concentric rings): every ring's centroid is the shared centre, which lies inside ALL the
 *  nested rings — so the OUTER ring would count its own descendants as "containing" it and get
 *  depth n instead of 0 (verified by the "3 concentric rings" test).  The vertex test gives the
 *  intended partial order: outer (radius 90) is inside nothing → 0; mid (radius 50) inside outer
 *  → 1; inner (radius 20) inside outer + mid → 2.  Overlapping-but-not-nested regions contain
 *  neither → equal depth → tie-break by id (deterministic). */
function resolveDominances(regions: MaterialRegion[]): void {
	const resolved = resolveRegionDominances(regions.map(r=>({
		id: r.id, polylines: r.polylines, bbox: r.bbox,
		mode: r.pathFeature.mode, explicitDominance: r.pathFeature.dominance,
	})));
	regions.forEach((r, i)=>r.dominance = resolved[i]);
	regions.sort((a, b)=> (b.dominance - a.dominance) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The §3.4 containment depth of `inner` among the given interior-bearing (non-stroke) regions.
 *  Kept as a thin wrapper over the shared generic machinery (`RegionDominance.ts`) so the Set12
 *  P7 UI + tests keep their `MaterialRegion`-typed entry point.  Pure — the bbox pre-filter +
 *  vertex test are the same ones `resolveDominances` uses, so a displayed depth always matches
 *  the resolved dominance for a feature without an explicit override. */
export function regionContainmentDepth(inner: MaterialRegion, interiorRegions: readonly MaterialRegion[]): number {
	return regionContainmentDepth_Gen(
		{id: inner.id, polylines: inner.polylines, bbox: inner.bbox, mode: inner.pathFeature.mode, explicitDominance: inner.pathFeature.dominance},
		interiorRegions.map(r=>({id: r.id, polylines: r.polylines, bbox: r.bbox, mode: r.pathFeature.mode, explicitDominance: r.pathFeature.dominance})),
	);
}

// ============================================================
// Signed-distance helpers (§3.3 — the rasterizer emits distances, the shader feathers them)
// ============================================================

// ============================================================
// Signed-distance helpers (§3.3 — the rasterizer emits distances, the shader feathers them)
// ============================================================

/** Distance from P to segment AB. PROMOTED from SplatRasterizer's private copy (plan §2) so the
 *  region bridge + rasterizer share one implementation. */
export function distPointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
	const dx = bx - ax, dy = by - ay;
	const lenSq = dx * dx + dy * dy;
	let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
	t = Math.max(0, Math.min(1, t));
	const cx = ax + t * dx, cy = ay + t * dy;
	const ex = px - cx, ey = py - cy;
	return Math.sqrt(ex * ex + ey * ey);
}

/** Distance from (x, y) to the nearest segment of the polyline. When `closed`, the last→first
 *  segment participates too (a closed path's stroke wraps the whole ring — the polyline's first
 *  point does NOT repeat the last, so the closing segment must be tested explicitly). */
export function distToPolyline(x: number, y: number, poly: Pt[], closed = false): number {
	return timeIt("distToPolyline", ()=>distToPolyline_inner(x, y, poly, closed));
}
/** The un-timed body of {@link distToPolyline} (wrapped above for timing). */
function distToPolyline_inner(x: number, y: number, poly: Pt[], closed = false): number {
	if (poly.length == 0) return Infinity;
	if (poly.length == 1) return distPointToSegment(x, y, poly[0].x, poly[0].y, poly[0].x, poly[0].y);
	let best = Infinity;
	for (let i = 0; i < poly.length - 1; i++) {
		best = Math.min(best, distPointToSegment(x, y, poly[i].x, poly[i].y, poly[i + 1].x, poly[i + 1].y));
	}
	if (closed) {
		best = Math.min(best, distPointToSegment(x, y, poly[poly.length - 1].x, poly[poly.length - 1].y, poly[0].x, poly[0].y));
	}
	return best;
}

/** Signed distance to a closed polygon: negative INSIDE (distance to the nearest edge), positive
 *  OUTSIDE. The two halves already exist (`pointInPolygon` + `distPointToSegment` over all edges,
 *  §3.3) — the closing edge is implicit (i+1 mod n). */
export function signedDistanceToPolygon(x: number, y: number, poly: Pt[]): number {
	return timeIt("signedDistanceToPolygon", ()=>signedDistanceToPolygon_inner(x, y, poly));
}
/** The un-timed body of {@link signedDistanceToPolygon} (wrapped above for timing). */
function signedDistanceToPolygon_inner(x: number, y: number, poly: Pt[]): number {
	if (poly.length < 3) return Infinity;
	let minDist = Infinity;
	for (let i = 0; i < poly.length; i++) {
		const a = poly[i], b = poly[(i + 1) % poly.length];
		const d = distPointToSegment(x, y, a.x, a.y, b.x, b.y);
		if (d < minDist) minDist = d;
	}
	return pointInPolygon(x, y, poly) ? -minDist : +minDist;
}

/** The per-region signed distance at a sample (§3.3): the THREE modes are ONE formula —
 *  `signedDistanceToPolygon(...) - strokeWidth/2` (strokeWidth 0 = plain fill), with the UNSIGNED
 *  polyline distance for open-path strokes. The region's overall distance = min over its
 *  polylines (a path can produce several meshes; inside ANY polygon = inside the region).
 *  Negative = inside, positive = outside (metres). `strokeWidth` is the FULL stroke width; the
 *  region extends `strokeWidth/2` from the polyline (a radius), so the field is shifted by half.
 *
 *  PERF — the rasterizer hot path (per-sample `regionDistanceAt` over every chunk-overlapping
 *  region) spends almost all its time inside this function: per edge it recomputes the segment's
 *  dx/dy/lenSq, rescans the polyline for `pointInPolygon`, and re-does it all for EVERY sample.
 *  The distance field is built ONCE per (region, chunk) via {@link buildRegionDistanceField}
 *  (geometry — flattened segment arrays + squared lengths + point bboxes — hoisted out of the
 *  sample loop, WeakMap-cached on the stable polyline arrays so a map's regions build it once
 *  total across all chunks and re-rasterises) and sampled per sample via
 *  {@link sampleRegionDistanceAt} (bbox-gate skips: a sample that cannot influence the distance
 *  returns without touching a single segment).  `regionDistanceAt` remains as the reference
 *  implementation (tests + non-rasteriser callers) — it is the build + sample in one.
 *
 *  Exactness note — the short-circuits are EXACT, not approximations: the region distance is a
 *  MIN over polylines, each polyline distance is a MIN over edges, and a sample outside a
 *  polyline's point-bbox is outside the polygon AND has every edge ≥ its bbox distance away —
 *  so skipping it (or skipping the ray-cast for an outside-bbox sample) never changes the min.
 *  The hoisted segment math is the reference's `distPointToSegment` verbatim (same t-clamp
 *  branches, same sqrt), and the hoisted ray-cast is `pointInPolygon` verbatim — the result is
 *  bit-identical per polyline. */
export function regionDistanceAt(
	mode: "fill" | "stroke" | "fillAndStroke",
	strokeWidth: number, // FULL width; the field shift is strokeWidth/2 (a radius). Ignored for "fill".
	polylines: Pt[][],
	closed: boolean,
	x: number, y: number,
): number {
	return sampleRegionDistanceAt(buildRegionDistanceField(mode, polylines, closed), x, y) - strokeWidth / 2;
}

/** The hoisted geometry of ONE polyline's distance field (see {@link RegionDistanceField}).
 *
 *  PERF pass 2 (2026-08-11) — pass 1 hoisted the sample-invariant segment math + added an exact
 *  point-bbox gate, but a sample INSIDE a polyline's bbox still paid O(edges) for the min-edge
 *  scan AND O(points) for the ray-cast.  That is the whole remaining cost: a chunk deep inside a
 *  big region (a lake / field covering many chunks) never hits the bbox gate, so all 33×33 = 1089
 *  samples scanned the full polyline.  Pass 2 adds two EXACT acceleration structures, both built
 *  once per polyline (WeakMap-cached with the rest of the field):
 *   - a uniform CELL GRID over the segments (`gStart`/`gSegs`, CSR; every segment registered in
 *     every cell its bbox overlaps).  The nearest-edge query walks Chebyshev RINGS outward from
 *     the sample's cell and stops as soon as the running best is ≤ the ring's provable lower
 *     bound `(r-1)·gCell` — so a far-from-boundary sample touches a handful of segments instead
 *     of all of them.  Exact: every segment is reachable (out-of-range indices clamp into the
 *     border cells) and the stop condition can only skip segments that are provably no closer.
 *   - a per-Y-BAND edge bucket for the ray-cast (`rcStart`/`rcSegs`, CSR over the same grid rows).
 *     Only edges whose y-interval covers the sample's y can flip the parity (`(yi > y) !== (yj > y)`
 *     requires y strictly between them), so scanning the band's bucket is exact — every other
 *     edge contributes nothing.  Null = fall back to the linear ray-cast (degenerate geometry).
 *
 *  Everything else is pass 1's contract, unchanged: `closed` is baked at build time (the closing
 *  edge is a segment like any other), and `strokeWidth` is NOT in the field — it is a constant
 *  shift applied by the sampler's caller (`min over polylines of (d - sw)` = `(min over polylines
 *  of d) - sw`), which is what makes the field shareable across regions that reference the same
 *  polyline array with different stroke widths.  Zero-length edges have lenSq 0 (degenerate
 *  segment → t = 0 → the distance to the shared point; the reference's `lenSq > 0` guard matches). */
export interface PolylineDistanceField {
	/** Per segment i: the a→b delta + its squared length (a = point i, b = point (i+1) % nPts). */
	segsX: Float64Array;
	segsY: Float64Array;
	segsLenSq: Float64Array;
	/** The polyline's points (the ray-cast ring + each segment's start point). */
	ptsX: Float64Array;
	ptsY: Float64Array;
	/** How many of the wrap-around segments participate (`segsX.length`; stroke-open drops the closing edge). */
	segCount: number;
	/** Point bbox — the per-sample skip gate (exact; see {@link sampleRegionDistanceAt}). */
	ptMinX: number; ptMinY: number; ptMaxX: number; ptMaxY: number;
	/** Uniform segment grid: origin = (ptMinX, ptMinY), `gCols × gRows` cells of side `gCell`. */
	gCols: number; gRows: number; gCell: number; gInvCell: number;
	/** CSR: cell c holds `gSegs[gStart[c] .. gStart[c+1])` (segment indices). */
	gStart: Int32Array;
	gSegs: Int32Array;
	/** CSR ray-cast buckets over the grid ROWS (band b = the y-slab of grid row b); entries are
	 *  RAY-CAST edge indices i (edge = point i → point i-1, the reference's pairing).  Null when
	 *  the bucketing would not pay (stroke mode — no ray-cast — or a degenerate blow-up). */
	rcStart: Int32Array|null;
	rcSegs: Int32Array|null;
}

/** The hoisted geometry of a region's distance field — built once per region by
 *  {@link buildRegionDistanceField}, sampled per sample by {@link sampleRegionDistanceAt}.
 *  Per polyline: {@link PolylineDistanceField}. */
export interface RegionDistanceField {
	mode: "fill" | "stroke" | "fillAndStroke";
	polylines: PolylineDistanceField[];
}

/** WeakMap cache for {@link RegionDistanceField} polylines, keyed on the polyline ARRAY
 *  reference.  The bridge's `region.polylines` is a fresh outer array per `collectMaterialRegions`
 *  call (its `.filter`), but the inner arrays ARE the stable `_meshSet_world` arrays of the
 *  packaged map object — so the cache survives both across-chunk re-rasterises AND across
 *  re-collects (a path edit rebuilds `_meshSet_world` → a new array → cache miss → rebuild).
 *  Memory is bounded by the live geometry (WeakMap keys don't leak). */
const polylineFieldCache = new WeakMap<Pt[], {mode: "fill"|"stroke"|"fillAndStroke"; closed: boolean; polyline: PolylineDistanceField}>();

/** Build the hoisted distance field for a region (see {@link RegionDistanceField}).  The
 *  per-polyline skip mirrors `regionDistanceAt`'s length guards (`< 2` skipped) — hoisted here
 *  so the reference stays in sync by construction.  Cached per polyline (see
 *  {@link polylineFieldCache}) — the cache key folds mode + closed (they change the segment
 *  set); strokeWidth is NOT folded in (see the interface docblock). */
export function buildRegionDistanceField(
	mode: "fill" | "stroke" | "fillAndStroke",
	polylines: Pt[][],
	closed: boolean,
): RegionDistanceField {
	const field: RegionDistanceField = {mode, polylines: []};
	for (const poly of polylines) {
		// Length guards mirror the reference: `distToPolyline`/`signedDistanceToPolygon` skip
		// polylines shorter than the minimum usable length (< 2 for stroke — a 1-point stroke
		// degenerates to a point distance; < 3 for fill — a 2-point polygon has no interior).
		if (mode == "stroke" ? poly.length < 2 : poly.length < 3) continue;
		const cached = polylineFieldCache.get(poly);
		if (cached != null && cached.mode == mode && cached.closed == closed) {
			field.polylines.push(cached.polyline);
			continue;
		}
		const polyline = buildPolylineField(poly, mode, closed);
		polylineFieldCache.set(poly, {mode, closed, polyline});
		field.polylines.push(polyline);
	}
	return field;
}

/** Target average number of segments per grid cell — the grid's cell side is chosen so the whole
 *  polyline lands in roughly `segCount / this` cells.  ~2 keeps the per-ring segment count small
 *  while bounding the grid's memory to O(segCount) (each segment registers in ≥ 1 cell). */
const GRID_TARGET_SEGS_PER_CELL = 2;
/** Hard cap on grid cells per polyline (memory guard for pathological aspect ratios). */
const GRID_MAX_CELLS = 1 << 16;
/** Grid-walk gates (see {@link polylineScan}): below these segment counts the flat scan wins. */
const GRID_MIN_SEGS_CAPPED = 48;
const GRID_MIN_SEGS_UNCAPPED = 512;

function buildPolylineField(poly: Pt[], mode: "fill"|"stroke"|"fillAndStroke", closed: boolean): PolylineDistanceField {
	// Stroke: the closing edge participates only when `closed` (the reference's distToPolyline —
	// an OPEN polyline's last segment ends at the last point).  Fill/fillAndStroke: the closing
	// edge is implicit (i+1 mod n — the reference's signedDistanceToPolygon).  In BOTH cases the
	// segment set is "a→b with b = (i+1) mod n over the first m entries" — m differs only.
	const nPts = poly.length;
	const m = mode == "stroke" ? nPts - 1 + (closed ? 1 : 0) : nPts;
	const segsX = new Float64Array(m);
	const segsY = new Float64Array(m);
	const segsLenSq = new Float64Array(m);
	for (let i = 0; i < m; i++) {
		const a = poly[i], b = poly[(i + 1) % nPts];
		const dx = b.x - a.x, dy = b.y - a.y;
		segsX[i] = dx; segsY[i] = dy;
		segsLenSq[i] = dx * dx + dy * dy;
	}
	const ptsX = new Float64Array(nPts);
	const ptsY = new Float64Array(nPts);
	let ptMinX = Infinity, ptMinY = Infinity, ptMaxX = -Infinity, ptMaxY = -Infinity;
	for (let i = 0; i < nPts; i++) {
		const p = poly[i];
		ptsX[i] = p.x; ptsY[i] = p.y;
		if (p.x < ptMinX) ptMinX = p.x;
		if (p.y < ptMinY) ptMinY = p.y;
		if (p.x > ptMaxX) ptMaxX = p.x;
		if (p.y > ptMaxY) ptMaxY = p.y;
	}

	// ---- Uniform segment grid (nearest-edge acceleration; see PolylineDistanceField) ----
	const spanX = Math.max(ptMaxX - ptMinX, 1e-9);
	const spanY = Math.max(ptMaxY - ptMinY, 1e-9);
	const targetCells = Math.max(1, Math.ceil(m / GRID_TARGET_SEGS_PER_CELL));
	// cell = sqrt(area / targetCells) → ~targetCells cells covering the bbox.
	let gCell = Math.sqrt((spanX * spanY) / targetCells);
	if (!(gCell > 0) || !Number.isFinite(gCell)) gCell = Math.max(spanX, spanY);
	let gCols = Math.max(1, Math.min(4096, Math.ceil(spanX / gCell)));
	let gRows = Math.max(1, Math.min(4096, Math.ceil(spanY / gCell)));
	while (gCols * gRows > GRID_MAX_CELLS) { // pathological aspect ratio — coarsen
		gCell *= 2;
		gCols = Math.max(1, Math.min(4096, Math.ceil(spanX / gCell)));
		gRows = Math.max(1, Math.min(4096, Math.ceil(spanY / gCell)));
	}
	// Recompute gCell so the grid EXACTLY covers the bbox (the ring lower-bound proof needs the
	// cell side to be the true stride in BOTH axes — take the max of the two so a ring's
	// Chebyshev distance in cells is a valid lower bound in metres for both axes).
	gCell = Math.max(spanX / gCols, spanY / gRows);
	const gInvCell = 1 / gCell;
	const cellCount = gCols * gRows;
	// Pass 1: count per cell.  Pass 2: fill (CSR).
	const counts = new Int32Array(cellCount + 1);
	const segCellRange = new Int32Array(m * 4); // c0, c1, r0, r1 per segment
	for (let i = 0; i < m; i++) {
		const ax = ptsX[i], ay = ptsY[i];
		const bx = ax + segsX[i], by = ay + segsY[i];
		const c0 = clampIdx(Math.floor((Math.min(ax, bx) - ptMinX) * gInvCell), gCols);
		const c1 = clampIdx(Math.floor((Math.max(ax, bx) - ptMinX) * gInvCell), gCols);
		const r0 = clampIdx(Math.floor((Math.min(ay, by) - ptMinY) * gInvCell), gRows);
		const r1 = clampIdx(Math.floor((Math.max(ay, by) - ptMinY) * gInvCell), gRows);
		segCellRange[i * 4] = c0; segCellRange[i * 4 + 1] = c1;
		segCellRange[i * 4 + 2] = r0; segCellRange[i * 4 + 3] = r1;
		for (let r = r0; r <= r1; r++) {
			for (let c = c0; c <= c1; c++) counts[r * gCols + c + 1]++;
		}
	}
	for (let c = 0; c < cellCount; c++) counts[c + 1] += counts[c];
	const gStart = counts; // now the CSR offsets (length cellCount+1)
	const gSegs = new Int32Array(gStart[cellCount]);
	const cursor = new Int32Array(cellCount);
	for (let i = 0; i < m; i++) {
		const c0 = segCellRange[i * 4], c1 = segCellRange[i * 4 + 1];
		const r0 = segCellRange[i * 4 + 2], r1 = segCellRange[i * 4 + 3];
		for (let r = r0; r <= r1; r++) {
			for (let c = c0; c <= c1; c++) {
				const cell = r * gCols + c;
				gSegs[gStart[cell] + cursor[cell]++] = i;
			}
		}
	}

	// ---- Ray-cast Y-band buckets (fill modes only — stroke never ray-casts) ----
	let rcStart: Int32Array|null = null;
	let rcSegs: Int32Array|null = null;
	if (mode != "stroke" && nPts >= 3) {
		// Ray-cast edge i pairs point i with point j = (i - 1 + nPts) % nPts (the reference's
		// `for (i=0, j=n-1; i<n; j=i++)` pairing).  The parity flip needs y strictly between
		// yi and yj, so edge i is only relevant to y-bands overlapping [min(yi,yj), max(yi,yj)].
		const rcCounts = new Int32Array(gRows + 1);
		const rcRange = new Int32Array(nPts * 2);
		for (let i = 0, j = nPts - 1; i < nPts; j = i++) {
			const yi = ptsY[i], yj = ptsY[j];
			const r0 = clampIdx(Math.floor((Math.min(yi, yj) - ptMinY) * gInvCell), gRows);
			const r1 = clampIdx(Math.floor((Math.max(yi, yj) - ptMinY) * gInvCell), gRows);
			rcRange[i * 2] = r0; rcRange[i * 2 + 1] = r1;
			for (let r = r0; r <= r1; r++) rcCounts[r + 1]++;
		}
		for (let r = 0; r < gRows; r++) rcCounts[r + 1] += rcCounts[r];
		const total = rcCounts[gRows];
		// Only pay for the buckets when they actually shrink the scan (a degenerate polyline
		// whose every edge spans every band would just duplicate the linear scan).
		if (total <= nPts * 8) {
			rcStart = rcCounts;
			rcSegs = new Int32Array(total);
			const rcCursor = new Int32Array(gRows);
			for (let i = 0; i < nPts; i++) {
				const r0 = rcRange[i * 2], r1 = rcRange[i * 2 + 1];
				for (let r = r0; r <= r1; r++) rcSegs[rcStart[r] + rcCursor[r]++] = i;
			}
		}
	}

	return {
		segsX, segsY, segsLenSq, ptsX, ptsY, segCount: m,
		ptMinX, ptMinY, ptMaxX, ptMaxY,
		gCols, gRows, gCell, gInvCell, gStart, gSegs,
		rcStart, rcSegs,
	};
}

function clampIdx(v: number, n: number): number {
	return v < 0 ? 0 : v >= n ? n - 1 : v;
}

/** Sample the precomputed distance field at (x, y) — the hot-path replacement for
 *  {@link regionDistanceAt} (§3.3; the caller subtracts `strokeWidth`).  Per polyline, the
 *  bbox gate is EXACT (see the builder's docblock): a sample outside the point-bbox skips the
 *  polyline entirely (strokes: every edge is ≥ its bbox distance away, so the min can't
 *  improve; fills: it's outside the polygon AND every edge is ≥ its bbox distance away — a
 *  positive value that can't beat an already-negative best).  An inside-bbox fill sample gets
 *  the min-edge scan + the hoisted ray-cast (same algorithm, same result as the reference); an
 *  inside-bbox sample with `best` already negative breaks the edge scan the moment an edge
 *  closer than |best| is found (the polyline's signed value can't improve `best` either way).
 *  The region's overall distance = min over its polylines — the reference's semantics verbatim. */
export function sampleRegionDistanceAt(field: RegionDistanceField, x: number, y: number, capDist = Infinity): number {
	const isStroke = field.mode == "stroke";
	const capSq = capDist == Infinity ? Infinity : capDist * capDist;
	let best = Infinity;
	for (const pl of field.polylines) {
		// Distance to the polyline's point-bbox (0 inside — a conservative lower bound for every
		// segment's distance; the closing edge is inside the bbox too).
		const bx = x < pl.ptMinX ? pl.ptMinX - x : x > pl.ptMaxX ? x - pl.ptMaxX : 0;
		const by = y < pl.ptMinY ? pl.ptMinY - y : y > pl.ptMaxY ? y - pl.ptMaxY : 0;
		const dToBboxSq = bx * bx + by * by;
		if (isStroke) {
			// Unsigned: every edge ≥ dToBbox, so no edge can beat `best` (exact — a tie doesn't change the min).
			if (best != Infinity && dToBboxSq >= best * best) continue;
			if (dToBboxSq > capSq) { if (best == Infinity) best = Math.sqrt(dToBboxSq); continue; } // beyond the cap band — any over-cap value clamps the same
			const dSq = Math.sqrt(polylineScan(pl, x, y, -1, capSq));
			if (dSq < best) best = dSq;
			continue;
		}
		// Fill / fillAndStroke.  Skip when the polyline can't improve `best`: outside the bbox
		// ⇒ outside the polygon ⇒ value +eMin ≥ dToBbox; if best < 0 any positive value loses, if
		// best ≥ 0 then +eMin ≥ dToBbox ≥ best.  (Inside the bbox, dToBboxSq == 0 → never skips.)
		if (dToBboxSq > 0 && (best < 0 || dToBboxSq >= best * best)) continue;
		// Outside the bbox by more than the cap ⇒ outside the polygon at ≥ cap ⇒ the caller's
		// clamp maps it to +cap regardless of the exact value (`best` is ≥ 0 here — see above).
		if (dToBboxSq > capSq) { if (capDist < best) best = capDist; continue; }
		const eMinSq = polylineScan(pl, x, y, best < 0 ? best * best : -1, capSq);
		if (best < 0) {
			// `scan.edgeWithinBest`: some edge was ≤ |best| away → the polyline's signed value
			// (-eMin or +eMin) is ≥ best either way → skip (no ray-cast needed).  Otherwise only
			// an INSIDE sample improves best (-eMin < best); +eMin > 0 > best can't.
			if (scan_edgeWithinBest) continue;
			if (pointInPolygonFromArrays(x, y, pl)) best = -Math.sqrt(eMinSq);
			continue;
		}
		// best ≥ 0 (no interior found yet): the polyline's signed value ∈ {+eMin, -eMin}; both
		// can improve.  The ray-cast decides the sign — only needed when the sample is inside
		// the bbox (dToBboxSq == 0); an outside-bbox sample is +eMin only.
		const inside = dToBboxSq == 0 && pointInPolygonFromArrays(x, y, pl);
		const eMin = Math.sqrt(eMinSq);
		best = inside ? -eMin : Math.min(best, eMin);
	}
	return best;
}

/** PERF pass 2b — the PER-CHUNK constant-distance short-circuit: when a whole chunk sits far
 *  enough from a region's every boundary edge, the region's distance is CONSTANT-under-clamping
 *  across all of the chunk's samples (33×33 = 1089 of them), so the rasteriser can resolve the
 *  region once per chunk instead of once per sample.  Returns that constant, or `null` when the
 *  chunk is close enough to a boundary that the per-sample path is required.
 *
 *  This is the case the pass-2 per-sample cap could not collapse: a viewport deep inside a huge
 *  plains / lake fill (the user's stated common case) is uniformly over-cap but NEGATIVE, so it
 *  never hit the "outside the bbox" gate and paid the full ring walk + ray-cast per sample.
 *
 *  Soundness: let `mE` = the min distance from the chunk rect to ANY segment of ANY of the
 *  region's polylines.  When `mE > capDist`:
 *   - no boundary crosses the rect, and the rect is connected, so every sample lies on the SAME
 *     side of each polyline as the rect's centre — one ray-cast per polyline classifies them all;
 *   - every sample's |distance| ≥ mE > capDist, so every consumer saturates: the returned
 *     ±capDist and the true value clamp identically (`clampDistanceForStorage`), and
 *     `regionFalloff` / `maskedDistance` saturate at |d| ≥ feather ≤ cap.  The SIGN is exact.
 *  This is the SAME contract as {@link sampleRegionDistanceAt}'s `capDist` (in-band exact;
 *  out-of-band = same sign, magnitude ≥ cap) — so it composes with it, and the caller needs no
 *  extra reasoning.  `mE` is computed exactly (rect↔segment distance between two convex shapes
 *  is attained at a vertex of one), at O(segments) ONCE per (region, chunk) — the cost of ~1
 *  sample, replacing 1089. */
export function regionDistanceOverRect(
	field: RegionDistanceField,
	rx: number, ry: number, rw: number, rh: number,
	capDist: number,
): number|null {
	if (field.polylines.length == 0 || !(capDist < Infinity)) return null;
	const rx2 = rx + rw, ry2 = ry + rh;
	let insideAny = false;
	for (const pl of field.polylines) {
		// Cheap bbox reject first: rect↔point-bbox distance is a lower bound for every segment,
		// so an over-cap bbox gap means this polyline is uniformly outside (no ray-cast needed).
		const gx = rx > pl.ptMaxX ? rx - pl.ptMaxX : pl.ptMinX > rx2 ? pl.ptMinX - rx2 : 0;
		const gy = ry > pl.ptMaxY ? ry - pl.ptMaxY : pl.ptMinY > ry2 ? pl.ptMinY - ry2 : 0;
		if (gx * gx + gy * gy > capDist * capDist) continue; // uniformly outside this polyline
		if (minRectToSegmentDistance(pl, rx, ry, rx2, ry2) <= capDist) return null; // boundary too close
		// Uniformly on one side; the centre's classification is the whole rect's.
		if (field.mode != "stroke" && pointInPolygonFromArrays((rx + rx2) / 2, (ry + ry2) / 2, pl)) insideAny = true;
	}
	return insideAny ? -capDist : capDist;
}

/** Exact min distance from the axis-aligned rect to the polyline's nearest segment.  For two
 *  disjoint CONVEX shapes the minimum is attained at a vertex of one of them, so the min over
 *  (each rect corner → segment) and (each segment endpoint → rect) is exact; an intersecting
 *  segment yields 0 via the endpoint-in-rect / corner-on-segment terms reaching 0. */
function minRectToSegmentDistance(pl: PolylineDistanceField, rx: number, ry: number, rx2: number, ry2: number): number {
	const {segsX, segsY, ptsX, ptsY, segCount} = pl;
	let best = Infinity;
	for (let i = 0; i < segCount; i++) {
		const ax = ptsX[i], ay = ptsY[i];
		const bx = ax + segsX[i], by = ay + segsY[i];
		// A segment straddling the rect (neither endpoint inside, no corner near) is caught by the
		// corner→segment terms: a crossing segment passes within 0 of some corner's projection.
		if (segmentIntersectsRect(ax, ay, bx, by, rx, ry, rx2, ry2)) return 0;
		const d = Math.min(
			distPointToSegment(rx, ry, ax, ay, bx, by), distPointToSegment(rx2, ry, ax, ay, bx, by),
			distPointToSegment(rx, ry2, ax, ay, bx, by), distPointToSegment(rx2, ry2, ax, ay, bx, by),
			distPointToRect(ax, ay, rx, ry, rx2, ry2), distPointToRect(bx, by, rx, ry, rx2, ry2),
		);
		if (d < best) { best = d; if (best == 0) return 0; }
	}
	return best;
}

function distPointToRect(px: number, py: number, rx: number, ry: number, rx2: number, ry2: number): number {
	const dx = px < rx ? rx - px : px > rx2 ? px - rx2 : 0;
	const dy = py < ry ? ry - py : py > ry2 ? py - ry2 : 0;
	return Math.sqrt(dx * dx + dy * dy);
}

/** Does segment AB touch the axis-aligned rect?  Slab clip (Liang–Barsky) on the segment's
 *  parameter — inclusive, so a grazing touch counts (conservative in the safe direction). */
function segmentIntersectsRect(ax: number, ay: number, bx: number, by: number, rx: number, ry: number, rx2: number, ry2: number): boolean {
	let t0 = 0, t1 = 1;
	const dx = bx - ax, dy = by - ay;
	for (let axis = 0; axis < 2; axis++) {
		const d = axis == 0 ? dx : dy;
		const p = axis == 0 ? ax : ay;
		const lo = axis == 0 ? rx : ry, hi = axis == 0 ? rx2 : ry2;
		if (d == 0) { if (p < lo || p > hi) return false; continue; }
		let tA = (lo - p) / d, tB = (hi - p) / d;
		if (tA > tB) { const tmp = tA; tA = tB; tB = tmp; }
		if (tA > t0) t0 = tA;
		if (tB < t1) t1 = tB;
		if (t0 > t1) return false;
	}
	return true;
}

/** Min SQUARED distance from (x, y) to the polyline's segments, with an exact early-out:
 *  `bestSqInit` ≥ 0 stops the scan the moment an edge ≤ sqrt(bestSqInit) is found (the caller
 *  then knows the polyline can't improve its running best) — `edgeWithinBest` reports that.
 *  Squared-distance comparison avoids a sqrt per edge (only the winning edge is sqrt'd by the
 *  caller).  The t-clamp + sqrt-free projection is the reference's `distPointToSegment` verbatim
 *  (lenSq 0 → t = 0 → the shared point).  Returns eMinSq; `edgeWithinBest` rides out on the
 *  module-scope {@link scan_edgeWithinBest} (a per-call result object showed up in the profile —
 *  this is called ~1M times per rasterise; single-threaded + fully synchronous, so it is safe). */
/** Out-param of {@link polylineScan} / {@link polylineScanLinear} — see their docblock. */
let scan_edgeWithinBest = false;

function polylineScan(
	pl: PolylineDistanceField,
	x: number, y: number,
	bestSqInit: number,
	capSq = Infinity,
): number {
	// PERF pass 2 — GRID RING SEARCH instead of the full O(edges) scan.  Walk Chebyshev rings of
	// grid cells outward from the sample's cell, accumulating the min squared segment distance,
	// and stop as soon as the running min is ≤ the next ring's provable lower bound.
	//
	// Exactness proof: (a) COMPLETENESS — every segment is registered in every grid cell its
	// bbox overlaps, and out-of-bbox extents clamp into the border cells, so a segment reachable
	// at ring r is ALSO registered in the nearest cell it touches; the ring walk therefore sees
	// it at the earliest ring any of its cells occupies.  (b) SOUNDNESS OF THE STOP — the sample
	// lies in (or is clamped to) cell (cx, cy); a cell at Chebyshev cell-distance r ≥ 1 has every
	// point at least (r-1)·gCell metres away (gCell is the max of the two axis strides, so this
	// bound holds on both axes).  Having finished ring r, the unvisited cells are at ring ≥ r+1,
	// i.e. ≥ r·gCell away — so a running min ≤ (r·gCell)² cannot be improved.  (c) The per-segment
	// math is pass 1's verbatim (which was the reference's `distPointToSegment` verbatim), and the
	// MIN over a set is order-independent, so the returned value is bit-identical to the reference.
	//
	// `edgeWithinBest` keeps its pass-1 meaning ("some segment is ≤ sqrt(bestSqInit) away") — a
	// SET property, so evaluating it under a different visit order is equivalent.  When it is
	// true the caller ignores `eMinSq` (it `continue`s), so returning the partial min is fine.
	const {segsX, segsY, segsLenSq, ptsX, ptsY, segCount, gCols, gRows, gCell, gInvCell, gStart, gSegs} = pl;
	// The ring search only pays when it can STOP early: a sample deep inside a region has a large
	// true distance, so the rings expand until the bound catches up — revisiting most cells (and
	// each multi-cell segment several times).  With a cap the stop is guaranteed near the band; without
	// one, only a big polyline amortises the walk.  Below that, the flat scan is measurably faster.
	if (segCount < (capSq == Infinity ? GRID_MIN_SEGS_UNCAPPED : GRID_MIN_SEGS_CAPPED)) return polylineScanLinear(pl, x, y, bestSqInit);
	const cx = clampIdx(Math.floor((x - pl.ptMinX) * gInvCell), gCols);
	const cy = clampIdx(Math.floor((y - pl.ptMinY) * gInvCell), gRows);
	const maxRing = Math.max(cx, gCols - 1 - cx, cy, gRows - 1 - cy);
	let eMinSq = Infinity;
	// Scan one cell's segment bucket; returns true when the caller must early-out.
	const scanCell = (cell: number): boolean=>{
		const end = gStart[cell + 1];
		for (let k = gStart[cell]; k < end; k++) {
			const i = gSegs[k];
			const ax = ptsX[i], ay = ptsY[i];
			const dx = segsX[i], dy = segsY[i], lenSq = segsLenSq[i];
			const t = lenSq > 0 ? ((x - ax) * dx + (y - ay) * dy) / lenSq : 0;
			const tC = t < 0 ? 0 : t > 1 ? 1 : t;
			const ex = x - (ax + tC * dx), ey = y - (ay + tC * dy);
			const dSq = ex * ex + ey * ey;
			if (dSq < eMinSq) {
				if (bestSqInit >= 0 && dSq <= bestSqInit) return true;
				eMinSq = dSq;
			}
		}
		return false;
	};
	for (let ring = 0; ring <= maxRing; ring++) {
		const r0 = cy - ring, r1 = cy + ring;
		const c0 = cx - ring, c1 = cx + ring;
		// Only the ring's PERIMETER cells are new (inner cells were visited at smaller rings):
		// the FULL column span on the top/bottom rows, and just the two end columns in between.
		for (let r = r0; r <= r1; r++) {
			if (r < 0 || r >= gRows) continue;
			const rowBase = r * gCols;
			if (r == r0 || r == r1) {
				for (let c = c0 < 0 ? 0 : c0; c <= c1 && c < gCols; c++) {
					if (scanCell(rowBase + c)) { scan_edgeWithinBest = true; return eMinSq; }
				}
			} else {
				if (c0 >= 0 && scanCell(rowBase + c0)) { scan_edgeWithinBest = true; return eMinSq; }
				if (c1 != c0 && c1 < gCols && scanCell(rowBase + c1)) { scan_edgeWithinBest = true; return eMinSq; }
			}
		}
		// Nothing unvisited can be closer than `ring · gCell` (see (b) above).
		const bound = ring * gCell;
		const boundSq = bound * bound;
		if (eMinSq <= boundSq) break;
		// CAP band: once even the lower bound exceeds capSq, every remaining segment is beyond
		// the caller's clamp radius — return the bound as an over-cap witness.  The caller only
		// uses over-cap values through `clampDistanceForStorage`, which maps them all to ±cap.
		if (boundSq > capSq) { scan_edgeWithinBest = false; return eMinSq == Infinity ? boundSq : eMinSq; }
	}
	scan_edgeWithinBest = false;
	return eMinSq;
}

/** Flat O(segments) scan over the hoisted arrays — pass 1's loop, kept for the uncapped
 *  small-polyline case (see {@link polylineScan}'s gate).  Same math, same early-out semantics. */
function polylineScanLinear(pl: PolylineDistanceField, x: number, y: number, bestSqInit: number): number {
	const {segsX, segsY, segsLenSq, ptsX, ptsY, segCount} = pl;
	let eMinSq = Infinity;
	for (let i = 0; i < segCount; i++) {
		const ax = ptsX[i], ay = ptsY[i];
		const dx = segsX[i], dy = segsY[i], lenSq = segsLenSq[i];
		const t = lenSq > 0 ? ((x - ax) * dx + (y - ay) * dy) / lenSq : 0;
		const tC = t < 0 ? 0 : t > 1 ? 1 : t;
		const ex = x - (ax + tC * dx), ey = y - (ay + tC * dy);
		const dSq = ex * ex + ey * ey;
		if (dSq < eMinSq) {
			if (bestSqInit >= 0 && dSq <= bestSqInit) { scan_edgeWithinBest = true; return eMinSq; }
			eMinSq = dSq;
		}
	}
	scan_edgeWithinBest = false;
	return eMinSq;
}

/** Point-in-polygon on the hoisted point arrays (the reference's ray-cast, first/last-edge
 *  convention verbatim — see {@link pointInPolygon}), accelerated by the per-Y-band edge buckets
 *  when present.
 *
 *  Exactness: the parity flip requires `(yi > y) !== (yj > y)`, i.e. y STRICTLY between the
 *  edge's two endpoint y's — so only edges whose y-interval covers y can contribute, and an edge
 *  whose interval covers y necessarily lands in y's band bucket (the band index is a monotone
 *  floor of y, so `bandOf(minY_e) ≤ bandOf(y) ≤ bandOf(maxY_e)`; the clamp is monotone too).
 *  Every skipped edge would have contributed `intersect == false`.  A sample outside the bbox
 *  clamps into a border band and correctly reports `false` (no edge there straddles it). */
function pointInPolygonFromArrays(x: number, y: number, pl: PolylineDistanceField): boolean {
	const {ptsX, ptsY, rcStart, rcSegs} = pl;
	const n = ptsX.length;
	let inside = false;
	if (rcStart != null && rcSegs != null) {
		const band = clampIdx(Math.floor((y - pl.ptMinY) * pl.gInvCell), pl.gRows);
		const end = rcStart[band + 1];
		for (let k = rcStart[band]; k < end; k++) {
			const i = rcSegs[k];
			const j = i == 0 ? n - 1 : i - 1;
			const xi = ptsX[i], yi = ptsY[i];
			const xj = ptsX[j], yj = ptsY[j];
			if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
		}
		return inside;
	}
	for (let i = 0, j = n - 1; i < n; j = i++) {
		const xi = ptsX[i], yi = ptsY[i];
		const xj = ptsX[j], yj = ptsY[j];
		const intersect = ((yi > y) !== (yj > y))
			&& (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
		if (intersect) inside = !inside;
	}
	return inside;
}

/** JS mirror of the shader's `regionWeight` (§3.6): `1 - smoothstep(-feather, feather, d)`.
 *  `feather <= 0` = hard edge (step at the boundary). Used by the rasterizer to RANK candidates
 *  (top-K selection, §3.3 step 2); the fragment shader applies the same curve per-fragment. */
export function regionFalloff(d: number, feather: number): number {
	if (feather <= 0) return d < 0 ? 1 : 0;
	const t = Math.min(1, Math.max(0, (d + feather) / (feather * 2)));
	const s = t * t * (3 - 2 * t); // smoothstep(0, 1, t)
	return 1 - s;
}

/** Clamp a signed distance for storage: ±(feather + noiseAmp), keeping the band near-linear for
 *  the LINEAR filter.  Feather-0 (Set6 Jungle→Water fix): no band → return raw `d` — clamping to
 *  0 would erase the sign `regionWeight(d,0)=d<0` needs to paint the hard-edge region. */
export function clampDistanceForStorage(d: number, feather: number, noiseAmp = 0): number {
	const max = feather + noiseAmp;
	if (max <= 0) return d; // no band → the true signed distance is already the correct storage
	return Math.max(-max, Math.min(max, d));
}

/** P2 — rescale a region's signed distance into its slot's feather scale (Set9 Part B, plan §3.1):
 *  a slot carries ONE feather (`u_slotFeather`, the MAX over the slot's regions — the shader
 *  feathers each slot with that single value), so a region whose authored feather is NARROWER than
 *  the slot's would otherwise render with the slot's wider band (its authored fade widened ~2.5×
 *  in the live bug).  Because `regionWeight(d, f) = 1 - smoothstep(-f, f, d)` depends on `d/f`
 *  only, the mapping is a pure LINEAR SCALE:
 *
 *      d' = d · slotFeather / regionFeather
 *
 *  and then `regionWeight(d', slotFeather) == regionWeight(d, regionFeather)` EXACTLY for every
 *  `d` (a pure change of units).  For `regionFeather == 0` (where the scale factor is undefined)
 *  the rule is the SIGN-SATURATION bake (plan §3.2): the distance is saturated to ±`slotClampAmt`,
 *  making `regionWeight(d', slotFeather)` return exactly 1 inside and exactly 0 outside — a true
 *  step — because `slotClampAmt >= slotFeather` puts both saturated values at/beyond the
 *  smoothstep band's ends.  The two cases together cover every region, so feather NEVER forces a
 *  slot split (plan §4.3).
 *
 *  @param d The region's signed distance (metres; negative = inside) AFTER its mask bake.
 *  @param regionFeather The region's own authored feather (> 0 → linear scale; 0 → sign-saturate).
 *  @param slotFeather The slot's resolved feather (the MAX over the slot's regions).
 *  @param slotClampAmt The slot's storage clamp (must be ≥ slotFeather — the by-construction
 *   no-phantom invariant).  Used only for the feather-0 sign-saturation bake. */
export function rescaleRegionDistance(
	d: number,
	regionFeather: number,
	slotFeather: number,
	slotClampAmt: number,
): number {
	if (regionFeather > 0) {
		// Linear change of units: `regionWeight(d·(slotF/regF), slotF) == regionWeight(d, regF)`.
		return d * (slotFeather / regionFeather);
	}
	// Feather-0 hard-edge region on a (possibly) soft slot: sign-saturate so the shader's
	// smoothstep band reads it as a step — exact, per §3.2 (the same bake P6 uses for masks).
	return d < 0 ? -slotClampAmt : slotClampAmt;
}

// ============================================================
// P6 — MASKS (§3.5 — elevation/slope bands as weight multipliers)
// ============================================================

/** Per-mask blend factor at a sample: 1 inside the band, 0 outside, a `smoothstep` ramp across
 *  the mask's `feather` on EACH side.  A mask MULTIPLIES the region's weight (the §4.5 DECIDED
 *  option — start with the simple, honest version; folding into the SDF is a revisit-if-needed).
 *  `feather <= 0` = a hard edge (step at the band boundary).  The smoothstep calls stay
 *  `smoothstep(edge0, edge1, x)` with edge0 < edge1 (the inverted form is undefined in GLSL ES
 *  300 — the rasteriser mirror keeps the same shape so the Node lane and the shader agree).
 *
 *  The feather DEFAULT is kind-aware: an elevationBand's feather is in METRES (2 m — the
 *  region-feather's order), but a slopeBand's axis is DIMENSIONLESS (m/m) — a 2 m/m ramp would
 *  swallow a whole cliff (2·atan(2) ≈ 126° of feathering).  Absent feather on a slopeBand falls
 *  back to 0.1 m/m (~6° — "steep faces" edge softness), so the default is sane on both axes.
 *  @param maskValue The sample's value for the mask's axis: ELEVATION (m) for an
 *  `elevationBand`, SLOPE (m/m) for a `slopeBand`. */
export function maskFactorAt(mask: {kind?: "elevationBand"|"slopeBand"; min: number; max: number; feather?: number|n}, maskValue: number): number {
	const feather = mask.feather ?? (mask.kind == "slopeBand" ? 0.1 : 2);
	if (feather <= 0) return maskValue >= mask.min && maskValue <= mask.max ? 1 : 0;
	if (maskValue < mask.min) {
		const t = Math.min(1, Math.max(0, (maskValue - (mask.min - feather)) / feather));
		return t * t * (3 - 2 * t); // smoothstep(min - feather, min, v)
	}
	if (maskValue > mask.max) {
		const t = Math.min(1, Math.max(0, (maskValue - mask.max) / feather));
		return 1 - t * t * (3 - 2 * t); // 1 - smoothstep(max, max + feather, v)
	}
	return 1;
}

/** The COMPOSITE mask factor of a region at a sample: the PRODUCT of every mask's factor
 *  (absent/empty masks = 1 — the region applies everywhere). */
export function regionMaskFactor(masks: readonly MaterialMask[]|n, elevation: number, slope: number): number {
	if (masks == null || masks.length == 0) return 1;
	let f = 1;
	for (const m of masks) {
		f *= m.kind == "elevationBand" ? maskFactorAt(m, elevation) : maskFactorAt(m, slope);
		if (f <= 0) break;
	}
	return f;
}

/** P6 — convert a region's signed distance to a MASKED distance for storage: the signed distance
 *  `d'` whose `regionWeight(d', feather)` EQUALS `regionWeight(d, feather) * maskFactor` — the
 *  exact P6 "multiply weight" (plan §4.5) BAKED into the distance field, so:
 *   - the shader needs NO mask data (no elevation plumbing, no params-texture mask block — the
 *     region-params row layout is unchanged from P5);
 *   - the LINEAR filter interpolates the baked field per fragment → the mask's own feather edges
 *     are per-fragment soft (the P3 distance-field philosophy: the splat lattice's texel size is
 *     a fixed screen-px size the user controls — not a fixed distance in metres — and a smooth
 *     distance can carry a mask edge that a hard field couldn't);
 *   - a maskless region (factor 1) is the IDENTITY → P5 outputs are byte-identical.
 *
 *  The inverse of the P3 smoothstep feather: `regionWeight(d) = 1 - smoothstep(-f, f, d)` (the
 *  smoothstep is `3u² - 2u³` at the band's parametric position `u = (d + f)/(2f)`), so a target
 *  weight `w' = w * factor` maps back to a distance via the inverse of `3u² - 2u³ = v'`
 *  (v' = 1 - w').  The cubic's real root in [0, 1] (Cardano's casus irreducibilis — the
 *  `k = 1` branch): `u = 1/2 + cos(acos(1 - 2v')/3 - 2π/3)`.  The result is always ≥ the
 *  original distance (a factor < 1 pulls the material's effective edge toward the sample),
 *  clamped to the [-feather, feather] band so the packed field stays within the P3 clamp
 *  contract.  The maskless (factor ≥ 1) and hard-feather (no band to spread across) cases are
 *  the identity. */
export function maskedDistance(d: number, feather: number, maskFactor: number): number {
	if (maskFactor >= 1) return d;
	if (feather <= 0) return d; // hard edge: no band to spread the mask across
	const w0 = 1 - smoothstepT(d, feather);
	const vp = 1 - w0 * maskFactor; // the smoothstep OUTPUT the baked distance must produce
	// Inverse of smoothstep(u) = 3u² - 2u³ at vp ∈ [0, 1] (unique root in [0, 1]):
	// u = 1/2 + cos(acos(1 - 2·vp)/3 - 2π/3).  (The k=0 branch gives the WRONG root — it maps
	// vp=0 → u=1; verified numerically: the k=1 branch hits u=0 at vp=0, u=1/2 at vp=1/2, u=1
	// at vp=1.)
	const u = 0.5 + Math.cos(Math.acos(1 - 2 * vp) / 3 - (2 * Math.PI) / 3);
	const dp = feather * (2 * u - 1);
	return Math.max(-feather, Math.min(feather, dp));
}

/** `smoothstep(edge0, edge1, x)` for the standard [-1, 1] → [0, 1] band: the P3 shader's
 *  `regionWeight = 1 - smoothstep(-feather, feather, d)` uses this curve.  The rasteriser
 *  mirror of the GLSL (spec-clean: edge0 < edge1). */
function smoothstepT(d: number, feather: number): number {
	const t = Math.min(1, Math.max(0, (d + feather) / (feather * 2)));
	return t * t * (3 - 2 * t);
}
